import axios from 'axios'
import buildResourcePath from '@lib/buildResourcePath'
import UIServicesRegistry from '@lib/ui-services/UIServicesRegistry'
import XMessageUIService from '@lib/ui-services/XMessageUIService'

export class APIServiceBase {

    /**
     * @param {string} endpointControllerName
     */
    constructor(endpointControllerName) {
        // Strip slashes from the controller name.
        while (endpointControllerName.indexOf(endpointControllerName.length - 1) === '/') {
            endpointControllerName = endpointControllerName.substr(0, endpointControllerName.length - 1)
        }

        this.endpointControllerName = endpointControllerName

        scheduleAtDocumentReady(() => {
            this.xMessageUIService = UIServicesRegistry.getService(XMessageUIService)
        })
    }

    /**
     * Sends a request to specifed enpoint.
     *
     * @param {string} actionName
     * @param {string} methodName POST/GET
     * @param {object} data
     * @param {object} additionalOptions
     */
    sendRequest(actionName, methodName, data, responseType = '', additionalOptions = {}) {
        const requestURL = this.buildRequestURL(actionName);
        let opts = {
            ...additionalOptions,
            url: requestURL,
            method: methodName.toUpperCase(),
            data: data
        }

        if (!opts.headers) {
            opts.headers = {};
        }

        opts.headers['X-Requested-With'] = 'XMLHttpRequest';

        if (responseType) {
            opts.responseType = responseType
        }

        return this.wrapAxiosPromise(axios(opts))
    }

    /**
     * @param {Promise} promise
     * @return {Promise}
     */
    wrapAxiosPromise(promise) {

        // Taken from https://developer.mozilla.org/en-US/docs/Web/API/WindowBase64/Base64_encoding_and_decoding
        const base64Decode = str => {
            // Going backwards: from bytestream, to percent-encoding, to original string.
            return decodeURIComponent(atob(str).split('').map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
        };

        const handleResponseXMessage = headers => {
            let messages = headers['x-message']
            if (messages !== undefined) {
                messages = JSON.parse(base64Decode(messages))
                messages.forEach(message => {
                    this.xMessageUIService.pushNotification(message)
                })
            }
        };

        const handleResponseRedirect = headers => {
            let redirectURL = headers['x-redirectto']
            if (redirectURL !== undefined) {
                window.location = redirectURL
                return true
            }

            return false
        };

        return new Promise((resolve, reject) => {
            promise.then(response => {
                let willRedirect = handleResponseRedirect(response.headers)
                if (willRedirect === false) {
                    handleResponseXMessage(response.headers)
                    resolve(response)
                }
            }).catch(err => {
                if (err.response) {
                    let willRedirect = handleResponseRedirect(err.response.headers)
                    if (willRedirect === false) {
                        handleResponseXMessage(err.response.headers)
                        reject(err)
                    }
                } else {
                    throw err
                }
            })
        })
    }

    /**
     * @param {string} actionName
     * @return {string}
     */
    buildRequestURL(actionName) {
        return buildResourcePath(this.endpointControllerName + '/' + actionName)
    }

    /**
     * Sends a POST request to specified endpoint.
     *
     * @public
     * @param {string} actionName
     * @param {object} data
     * @return {Promise}
     */
    post(actionName, data, responseType = '') {
        return this.sendRequest(actionName, 'POST', data, responseType)
    }

    /**
     * Sends a GET request to specified endpoint.
     *
     * @public
     * @param {string} actionName
     * @param {object} data
     * @return {Promise}
     */
    get(actionName, data) {
        return this.sendRequest(actionName, 'GET', data)
    }
}

export class UnsupportedRequestMethodError extends Error {
}

export default APIServiceBase